import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import Modal from 'react-bootstrap-modal'
import FlashAlert from '../../../components/flash-alert'
import { ajaxGet, postForm, patchForm } from '../../../ajax'
import TextInput from '../inputs/text-input'
import MultiSelect from '../inputs/multi-select'
import CheckboxInput from '../inputs/checkbox-input'
import PhoneInputWrapper from '../inputs/phone-input'
import SelectInput from '../inputs/select-input'

export default function EditMember({leagueId, teamId, member, onClose, onUpdate, onDelete}) {
  const { register, reset, control,  handleSubmit, setValue, getValues, formState: { errors } } = useForm({})

  const [dataset, setDataset] = useState(null)
  const [player, setPlayer] = useState(null)
  const [countryCode, setCountryCode] = useState(null)
  const [alert, setAlert] = useState(null)
  const [fileName, setFileName] = useState(false)
  const [loading, setLoading] = useState(true)

  const imageUrl = getValues('image_url')
  const requiredMsg = 'Is required'
  const positionOptions = ['Forward', 'Winger', 'Center Midfielder', 'Right / Left Back', 'Center Back', 'Goalie'].map(val => ({value: val, label: val}))
  const skillLevelOptions = [['', 'Select'], [0, 'Beginner'], [1, 'Intermediate'], [2, 'Advanced'], [3, 'Expert']]
  const sizeOptions = [['', 'Select'], ['XS', 'XS'], ['S', 'S'], ['M', 'M'], ['L', 'L'], ['XL', 'XL'], ['XXL', 'XXL']]
  
  useEffect(() => {
    loadPlayer()
  }, [])

  useEffect(() => {
    if (dataset) reset(dataset)
  }, [dataset])

  const loadPlayer = () => {
    ajaxGet(`/admins/clients/${member.player_id}/edit.json`)
    .then(response => { if (response.ok) return response.json() })
    .then((json) => {
      const positions = json.player.prefer_position ? json.player.prefer_position.split(', ').map(p => ({ value: p, label: p })) : ''
      const player = {...json.player, prefer_position: positions}
    
      setPlayer(player)
      setDataset({...member, ..._.omit(player, 'id')})
      setLoading(false)
    })
  }
  
  const memberImage = () => {
    if (!imageUrl) return '/images/ic_user@2x.png'

    if (imageUrl && imageUrl.includes('data:')) return imageUrl
    else return imageUrl + '?' + Math.random()
  }

  const loadFile = (file) => {
    if (file.size > 10000000) return window.alert('File size limit is 10MB')
    if (!['image/jpeg', 'image/png'].includes(file.type)) return window.alert(' Only JPG and PNG files are allowed')

    const fr = new FileReader()
    fr.onloadend = function() { 
      setValue('image_url', fr.result)
      setFileName(file.name)
    }
    fr.readAsDataURL(file)
  }

  const fileLoaded = (files) => {
    if (files && files[0]) loadFile(files[0])
  }

  const validateName = (value) => {
    if (!value.trim()) return requiredMsg
  }

  const clickCancel = (e) => {
    e.preventDefault()
    onClose()
  }

  const clickDelete = (e) => {
    e.stopPropagation()
    if (window.confirm('Are you sure you want to delete this player?')) {
      onClose()
      onDelete(member)
    }
  }

  const prepareFormData = (data) => {
    let formData = new FormData()
    formData.append('team_id', teamId)
    formData.append('member[player_id]', data.player_id)
    formData.append('member[captain]', data.captain)
    formData.append('member[player_attributes][id]', data.player_id)
    formData.append('member[player_attributes][first_name]', data.first_name.trim())
    formData.append('member[player_attributes][last_name]', data.last_name.trim())
    formData.append('member[player_attributes][phone]', data.phone)
    if (countryCode) formData.append('member[player_attributes][country_code]', countryCode)
    formData.append('member[player_attributes][skill_level]', data.skill_level)
    formData.append('member[player_attributes][shirt_size]', data.shirt_size)
    formData.append('member[player_attributes][prefer_position]', data.prefer_position.map(v => v.label).join(', '))
    if (data.image_url) formData.append('member[player_attributes][image_url]', data.image_url)
    return formData
  }

  const submitUrl = (data) => {
    if (data.id) return `/admins/leagues/${leagueId}/members/${data.id}.json`
    return `/admins/leagues/${leagueId}/members.json`
  }

  const onSubmit = (data) => {
    setLoading(true)
    let formData = prepareFormData(data)
    const submitMethod = data.id ? patchForm : postForm

    submitMethod(submitUrl(data), formData)
    .then(response => { return response.json() })
    .then((json) => {
      setLoading(false)
      if (json.alert)
        setAlert(json.alert)
      else {
        onUpdate(json.member)
      }
    })
  }

  return <Modal container={document.body} backdrop='static' show={true} onHide={onClose} dialogClassName='new-team-modal'>
    <Modal.Header closeButton>
      <Modal.Title>Edit Player</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      { loading && <div className='animated-spinner'></div> }
      { player &&
        <form className={'edit-member-form' + (loading ? ' isLoading' : '')} onSubmit={handleSubmit(onSubmit)}>
          <div className='member-photo'>
            <input 
              type='file'
              accept='image/png,image/jpeg'
              id='member-photo-input'
              onChange={(e) => fileLoaded(e.target.files)}
            />
            <label id='member-photo-label' htmlFor='member-photo-input'>
              <img alt='Icon' src={memberImage()} />
            </label>
          </div>
          { alert && <FlashAlert text={alert.text} type={alert.type} onHide={() => setAlert(null)} />}
          <div className='member-fields'>
            <TextInput
              {...register('first_name', { required: requiredMsg, validate: validateName })}
              label='First Name'
              error={errors.first_name}
              onChange={e => setValue('first_name', e.target.value)}
            />
            <TextInput
              {...register('last_name', { required: requiredMsg, validate: validateName })}
              label='Last Name'
              error={errors.last_name}
              onChange={e => setValue('last_name', e.target.value)}
            />
          </div>
          <CheckboxInput
            {...register('captain')}
            label='Captain'
            onChange={e => setValue('captain', e.target.checked)}
          />
          <div className='member-fields'>
            <PhoneInputWrapper
              {...register('phone', { required: requiredMsg })}
              label='Phone'
              error={errors.phone}
              defaultValue={member.phone}
              onChange={(p, c) => {
                setValue('phone', p)
                setCountryCode(c)
              }}
            />
            <MultiSelect
              name='prefer_position'
              label='Preferred Position'
              required
              error={errors.prefer_position}
              options={positionOptions}
              control={control}
            />
          </div>
          <div className='member-fields'>
            <SelectInput
              {...register('skill_level', { required: requiredMsg })}
              label='Skill Level'
              options={skillLevelOptions}
              error={errors.skill_level}
              onChange={e => setValue('skill_level', e.target.value)}
            />
            <SelectInput
              {...register('shirt_size', { required: requiredMsg })}
              label='Shirt Size'
              options={sizeOptions}
              error={errors.shirt_size}
              onChange={e => setValue('shirt_size', e.target.value)}
            />
          </div>
          <div className='member-delete' onClick={clickDelete}>Delete player</div>
          <div className='edit-member-buttons'>
            <button className='member-button button-cancel' onClick={clickCancel}>Cancel</button>
            <button className='member-button button-save' type='submit'>Save</button>
          </div>
        </form>
      }
    </Modal.Body>
  </Modal>
}
